import { Badge, Box, Row, Tooltip } from "@hightouch/htui";
import { formatDistanceToNow } from "date-fns";

import { ContentImageThumbnail } from "src/components/llm/content-images/content-image-thumbnail";
import { SelectableAssetCard } from "src/components/asset-picker/selectable-asset-card";
import { ASSET_CARD_ASPECT_RATIO } from "src/components/asset-picker/constants";
import {
  AssetCardFooter,
  AssetCardMedia,
} from "src/components/asset-picker/asset-picker-primitives";

/** The minimum an image-library record needs to render as a picker card. */
export type ImageAssetCardImage = {
  id: string;
  name: string;
  presignedUrl: string;
  updatedAt?: string | null;
};

type ImageAssetCardProps<T extends ImageAssetCardImage> = {
  image: T;
  selected: boolean;
  onToggle: (image: T) => void;
  /** Hide the bulk-select checkbox for single-select pickers. */
  hideCheckbox?: boolean;
  /**
   * Disable the checkbox for an unselected card once the selection cap is hit.
   */
  checkboxDisabled?: boolean;
  /** Pills under the name, such as the image's brands where mixed brands share the grid. */
  badges?: string[];
};

export const ImageAssetCard = <T extends ImageAssetCardImage>({
  image,
  selected,
  onToggle,
  hideCheckbox,
  checkboxDisabled = false,
  badges = [],
}: ImageAssetCardProps<T>) => (
  <Box sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}>
    <SelectableAssetCard
      selected={selected}
      hideCheckbox={hideCheckbox}
      checkboxDisabled={checkboxDisabled}
      checkboxLabel={image.name}
      onToggleSelect={() => onToggle(image)}
      onClick={checkboxDisabled ? undefined : () => onToggle(image)}
      footer={
        <>
          <AssetCardFooter
            title={image.name}
            // Search results can lack a timestamp — skip the line rather than
            // formatting an invalid date.
            subtitle={
              image.updatedAt
                ? `Updated ${formatDistanceToNow(new Date(image.updatedAt), {
                    addSuffix: true,
                  })}`
                : undefined
            }
          />
          {badges.length > 0 && (
            <Row gap={1} flexWrap="wrap" minWidth={0}>
              {badges.map((badge) => (
                // The badge truncates a long name, so the tooltip keeps it readable.
                <Tooltip key={badge} message={badge}>
                  <Badge size="sm">{badge}</Badge>
                </Tooltip>
              ))}
            </Row>
          )}
        </>
      }
    >
      <AssetCardMedia matteTop={!hideCheckbox}>
        <ContentImageThumbnail
          imageId={image.id}
          presignedUrl={image.presignedUrl}
          thumbnailWidth={480}
          alt={image.name}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </AssetCardMedia>
    </SelectableAssetCard>
  </Box>
);
